import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectOrganizationsData } from '../../store/organization/organization-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { getDeploymentWeeksById } from '../../store/organization/organization-actions';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import Badge from '@mui/material/Badge';
import Stack from '@mui/material/Stack';
import { dateToStringFormat } from '@common/utils/date';
import { DownloadIcon, FilterIcon } from '@common/utils/icons/icons';
import FiltersDialog from './FiltersDialog';
import useFetchAll from '../../hooks/useFetchAll';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import Chip from '@mui/material/Chip';
import GeneralLoaderWrapper from '@common/components/General/GeneralLoaderWrapper';
import OrganizationData from './OrganizationData';
import { useSearchParams } from 'react-router-dom';
import { FilterKeys } from '@common/enums/query-params/filters';
import { convertReportParametersToQuery } from '../../models/general/notifications/report-parameters';
import { NotificationTrigger } from '../../models/enums/notifications';
import { resetOrganizationsRestState } from '../../store/organization/organization-slice';
import { selectRobotsData } from '../../store/robots/robots-slice-selectors';
import { getOrganizationRobots } from '../../store/robots/robots-actions';
import Typography from '@mui/material/Typography';
import { getCurrentPageName, trackEvent } from '../../services/analytics';
import { AnalyticsEvent } from '../../services/analytics-events';
import { describeFilters } from '../../services/analytics-filters';

const OrganizationDataDashboard: React.FC = () => {
  const { t } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filterParams, setFilterParams] = useSearchParams();
  const { fetchAll: queryGetAllStats, saveFetchAll, clearFetchAll } = useFetchAll('dashboard');
  const initialStart = filterParams.get(FilterKeys.StartDate);
  const initialEnd = filterParams.get(FilterKeys.EndDate);

  const dispatch = useAppDispatch();

  const {
    rest: { getDeploymentWeeksStatus, getError, getStatus },
    deploymentWeeks,
    current: organization,
  } = useAppSelector((state) => selectOrganizationsData(state));
  const robotsData = useAppSelector((state) => selectRobotsData(state));

  const [isFiltersDialogOpen, setIsFiltersDialogOpen] = useState(false);
  const [filteredRobotIds, setFilteredRobotIds] = useState<string[] | null>(null);
  const [weekFilter, setWeekFilter] = useState(() => ({
    start: initialStart ? DateTime.fromISO(initialStart) : null,
    end: initialEnd ? DateTime.fromISO(initialEnd) : null,
  }));

  useEffect(() => {
    if (robotsData.rest.getStatus === HttpFetchStatus.Idle && organization?.id != null) {
      dispatch(
        getOrganizationRobots({
          org: organization.id,
          all: false,
        }),
      );
    }
  }, [dispatch, organization?.id, robotsData.rest.getStatus]);

  useEffect(() => {
    if (getDeploymentWeeksStatus === HttpFetchStatus.Idle && organization?.id != null) {
      dispatch(
        getDeploymentWeeksById({
          id: organization.id,
          filters: {
            all: queryGetAllStats,
            start: weekFilter.start != null ? weekFilter.start.toISO()! : undefined,
            end: weekFilter.end != null ? weekFilter.end.toISO()! : undefined,
            robot_ids: filteredRobotIds?.join(','),
          },
        }),
      );
    }
  }, [
    dispatch,
    organization?.id,
    getDeploymentWeeksStatus,
    queryGetAllStats,
    weekFilter.start,
    weekFilter.end,
    filteredRobotIds,
  ]);

  useEffect(() => {
    dispatch(resetOrganizationsRestState({ getDeploymentWeeksStatus: HttpFetchStatus.Idle }));
  }, [dispatch, queryGetAllStats, weekFilter.start, weekFilter.end, filteredRobotIds]);

  const openFiltersDialogClickHandler = () => {
    setIsFiltersDialogOpen(true);
  };

  const closeFiltersDialogHandler = () => {
    setIsFiltersDialogOpen(false);
  };

  const trackFiltersChanged = (
    filterAction: string,
    filters: {
      start?: string | null;
      end?: string | null;
      robots?: string[] | null;
      all?: boolean;
    },
  ) => {
    trackEvent(AnalyticsEvent.FiltersChanged, {
      ...describeFilters(filters, 'start', 'end'),
      filter_action: filterAction,
      source_page: getCurrentPageName(),
    });
  };

  const updateFilteredRobotHandler = (ids: string[] | null) => {
    trackFiltersChanged('robots', {
      start: weekFilter.start?.toISO() ?? null,
      end: weekFilter.end?.toISO() ?? null,
      robots: ids,
      all: queryGetAllStats,
    });
    setFilteredRobotIds(ids);
  };

  const weekFilterChangeHandler = (week: { start?: DateTime; end?: DateTime }) => {
    trackFiltersChanged('date_range', {
      start: (week.start ?? weekFilter.start)?.toISO() ?? null,
      end: (week.end ?? weekFilter.end)?.toISO() ?? null,
      robots: filteredRobotIds,
      all: queryGetAllStats,
    });
    setWeekFilter((prevState) => ({ ...prevState, ...week }));
    setFilterParams((prevState) => {
      if (week.start) {
        prevState.set(FilterKeys.StartDate, week.start.toString());
      }
      if (week.end) {
        prevState.set(FilterKeys.EndDate, week.end.toString());
      }
      return prevState;
    });
  };

  const resetFiltersHandler = () => {
    trackFiltersChanged('reset', { all: queryGetAllStats });
    setWeekFilter({
      start: null,
      end: null,
    });
    clearFetchAll();
    setFilterParams((prevState) => {
      prevState.delete(FilterKeys.StartDate);
      prevState.delete(FilterKeys.EndDate);
      prevState.delete(FilterKeys.All);
      return prevState;
    });
    setFilteredRobotIds(null);
  };

  const toggleAllClickHandler = () => {
    trackFiltersChanged(queryGetAllStats ? 'show_filtered' : 'show_all', {
      start: weekFilter.start?.toISO() ?? null,
      end: weekFilter.end?.toISO() ?? null,
      robots: filteredRobotIds,
      all: !queryGetAllStats,
    });
    saveFetchAll(!queryGetAllStats);
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.All, (!queryGetAllStats).toString());
      return prevState;
    });
  };

  const downloadDashboardSummaryClickHandler = () => {
    if (organization?.id) {
      let startTime = new Date();
      if (weekFilter.start == null && deploymentWeeks != null) {
        deploymentWeeks.forEach((dw) => {
          const weekStartDate = new Date(dw.start);
          if (weekStartDate < startTime) {
            startTime = weekStartDate;
          }
        });
      }
      setFilterParams((prevState) => {
        prevState.set(
          FilterKeys.Report,
          convertReportParametersToQuery({
            trigger: NotificationTrigger.DASHBOARD_SUMMARY,
            organizationId: organization.id,
            startTime:
              weekFilter.start != null ? weekFilter.start.toISO()! : startTime.toISOString(),
            endTime:
              weekFilter.end != null
                ? weekFilter.end.toISO()!
                : DateTime.now().endOf('week').toISO()!,
            all: queryGetAllStats,
          }),
        );

        return prevState;
      });
    }
  };

  return (
    <Stack p={2} spacing={2}>
      <Typography variant="h2" fontSize={24} lineHeight={2}>
        {`${organization?.name ?? ''} - ${t('button-statistics')}`}
      </Typography>
      <GeneralLoaderWrapper
        getStatus={getStatus}
        loadingMessage={t('loading-organizations')}
        dataLoaded={organization != null}
        failedToLoadMessage={getError?.message ?? t('error-loading-current-organization')}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {weekFilter.start != null && weekFilter.end != null && (
              <Chip
                onClick={openFiltersDialogClickHandler}
                label={`${dateToStringFormat(weekFilter.start, true)} - ${dateToStringFormat(weekFilter.end, true)}`}
                variant="outlined"
              />
            )}
            {queryGetAllStats && (
              <Chip
                onDelete={toggleAllClickHandler}
                label={t('input-label-fetch-all')}
                variant="outlined"
              />
            )}
            <TooltipIconButton
              tooltip={t('info-tooltip-open-filters')}
              icon={
                <Badge variant="dot" invisible={filteredRobotIds == null} color="primary">
                  <FilterIcon color="primary" />
                </Badge>
              }
              onClick={openFiltersDialogClickHandler}
            />
            <TooltipIconButton
              tooltip={t('info-tooltip-download-report')}
              icon={<DownloadIcon color="primary" />}
              onClick={downloadDashboardSummaryClickHandler}
            />
          </Stack>
        </Stack>
        <OrganizationData
          deploymentWeeks={deploymentWeeks}
          weekFilter={weekFilter}
          filteredRobotIds={filteredRobotIds}
          loading={getDeploymentWeeksStatus === HttpFetchStatus.Loading}
        />
      </GeneralLoaderWrapper>
      <FiltersDialog
        open={isFiltersDialogOpen}
        onClose={closeFiltersDialogHandler}
        onResetFilter={resetFiltersHandler}
        weekFiltersHandler={{
          weekFilter: weekFilter,
          onWeekFilterChange: weekFilterChangeHandler,
        }}
        robotDeploymentHandler={{
          getStatus: getDeploymentWeeksStatus,
          deploymentWeeks: deploymentWeeks,
          filteredRobots: filteredRobotIds,
          onUpdateFilteredRobots: updateFilteredRobotHandler,
        }}
        allHandler={{ all: queryGetAllStats, onToggle: toggleAllClickHandler }}
        robotsData={robotsData.pagination?.data}
      />
    </Stack>
  );
};

export default OrganizationDataDashboard;
